type IdleCallback = () => void

declare global {
  interface IdleDeadline {
    readonly didTimeout: boolean
    timeRemaining(): DOMHighResTimeStamp
  }

  interface Window {
    requestIdleCallback?: (callback: IdleCallback | ((deadline: IdleDeadline) => void), options?: { timeout?: number }) => number
    cancelIdleCallback?: (handle: number) => void
  }
}

const isWindowAvailable = () => typeof window !== 'undefined'

/**
 * Schedules a task to run when the browser is idle (or after the timeout).
 * Returns a cleanup function that cancels the pending work.
 */
export function deferToIdle(task: IdleCallback, timeout = 1500) {
  if (!isWindowAvailable()) {
    task()
    return () => {}
  }
  let cancelled = false
  const win = window as Window
  let idleHandle: number | null = null
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null

  const runTask = () => {
    if (cancelled) return
    task()
  }

  if (typeof win.requestIdleCallback === 'function') {
    idleHandle = win.requestIdleCallback(() => runTask(), { timeout })
  } else {
    timeoutHandle = window.setTimeout(runTask, timeout)
  }

  return () => {
    cancelled = true
    if (idleHandle !== null && typeof win.cancelIdleCallback === 'function') {
      win.cancelIdleCallback(idleHandle)
    }
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle)
    }
  }
}

/**
 * Defers a task until after the window load event, then schedules it for the next idle period.
 * Useful for non-critical work that should not contend with initial rendering or LCP.
 */
export function deferToLoadAndIdle(task: IdleCallback, idleTimeout = 1500, loadDelay = 250) {
  if (!isWindowAvailable()) {
    task()
    return () => {}
  }

  let cancelled = false
  let cancelIdle: () => void = () => {}
  let loadTimer: ReturnType<typeof setTimeout> | null = null
  let cancelOnLoad: () => void = () => {}

  const startIdleWork = () => {
    if (cancelled) return
    cancelIdle = deferToIdle(task, idleTimeout)
  }

  const scheduleAfterLoad = () => {
    if (cancelled) return
    if (loadDelay > 0) {
      loadTimer = window.setTimeout(startIdleWork, loadDelay)
    } else {
      startIdleWork()
    }
  }

  if (document.readyState === 'complete') {
    scheduleAfterLoad()
  } else {
    const onLoad = () => scheduleAfterLoad()
    window.addEventListener('load', onLoad, { once: true })
    cancelOnLoad = () => window.removeEventListener('load', onLoad)
  }

  return () => {
    cancelled = true
    if (loadTimer !== null) {
      clearTimeout(loadTimer)
    }
    cancelIdle()
    cancelOnLoad()
  }
}
